//
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import { type Class, type Doc, type Ref } from '@hcengineering/core'
import { type Person } from '@hcengineering/contact'
import { type IntlString, type Plugin, plugin } from '@hcengineering/platform'
import { type Project } from '@hcengineering/tracker'
import type { AnyComponent } from '@hcengineering/ui'

/**
 * @public
 * Flat record with a `kind` discriminator rather than a class per type: a form
 * builder never needs the per-question behaviour that would justify separate
 * classes, and this keeps the whole schema in one attribute on IntakeForm.
 */
export enum IntakeFieldKind {
  Text = 'text',
  Multiline = 'multiline',
  Email = 'email',
  Number = 'number',
  Select = 'select'
}

/**
 * @public
 * Which part of the created Issue a field's answer feeds. Exactly one field
 * should map to `title`; everything else ends up in the submission comment.
 */
export type IntakeFieldTarget = 'title' | 'description' | 'submitterName' | 'submitterEmail' | 'none'

/**
 * @public
 */
export interface IntakeField {
  /** Stable key for the answer, independent of the label being renamed. */
  id: string
  label: string
  kind: IntakeFieldKind
  required: boolean
  placeholder?: string
  /** Choices, for Select. */
  options?: string[]
  target?: IntakeFieldTarget
}

/**
 * @public
 * A public, unauthenticated submission endpoint that creates an Issue in
 * `project` for every request bearing `token`. The submission itself never
 * gets a session in the platform — a standalone service (pod-intake)
 * connects as the system account and writes on the submitter's behalf, so
 * none of the anonymous-guest auth machinery is touched by this feature.
 */
export interface IntakeForm extends Doc {
  name: string
  project: Ref<Project>
  token: string
  enabled: boolean
  submissionCount: number
  /** User-defined fields. Empty/undefined falls back to the built-in defaults. */
  fields?: IntakeField[]
  /** Heading shown on the public page; defaults to `name`. */
  heading?: string
  /** Intro text shown under the heading. */
  intro?: string
  /** Shown after a successful submission. */
  successMessage?: string
  /**
   * Assigned to every issue this form creates. Notification comes for free: Huly already notifies
   * an assignee, so routing submissions at a person is also how that person hears about them.
   */
  defaultAssignee?: Ref<Person> | null
  /**
   * Telegram chat to ping on each submission. Needs INTAKE_TELEGRAM_BOT_TOKEN set on the service;
   * without it this is ignored. Separate from the in-app path on purpose -- a tracker nobody is
   * watching is not a notification channel.
   */
  telegramChatId?: string
}

/**
 * @public
 * What a form falls back to when no fields have been defined -- also the
 * starting point the editor offers, so the historical shape stays reproducible.
 */
export const defaultIntakeFields: IntakeField[] = [
  { id: 'title', label: 'Тема обращения', kind: IntakeFieldKind.Text, required: true, target: 'title' },
  { id: 'description', label: 'Описание', kind: IntakeFieldKind.Multiline, required: false, target: 'description' },
  { id: 'name', label: 'Ваше имя', kind: IntakeFieldKind.Text, required: false, target: 'submitterName' },
  { id: 'email', label: 'Email', kind: IntakeFieldKind.Email, required: false, target: 'submitterEmail' }
]

/**
 * @public
 */
export const intakeId = 'intake' as Plugin

export default plugin(intakeId, {
  class: {
    IntakeForm: '' as Ref<Class<IntakeForm>>
  },
  component: {
    IntakeForms: '' as AnyComponent,
    CreateIntakeForm: '' as AnyComponent,
    EditIntakeForm: '' as AnyComponent
  },
  ids: {
    Settings: '' as Ref<Doc>
  },
  string: {
    NewIntakeForm: '' as IntlString,
    Create: '' as IntlString,
    FormName: '' as IntlString,
    TargetProject: '' as IntlString,
    NoForms: '' as IntlString,
    PublicUrl: '' as IntlString,
    Submissions: '' as IntlString,
    Enabled: '' as IntlString,
    CopyUrl: '' as IntlString,
    Fields: '' as IntlString,
    EditFields: '' as IntlString,
    AddField: '' as IntlString,
    FieldLabel: '' as IntlString,
    FieldType: '' as IntlString,
    FieldRequired: '' as IntlString,
    FieldPlaceholder: '' as IntlString,
    FieldOptions: '' as IntlString,
    FieldTarget: '' as IntlString,
    TargetNone: '' as IntlString,
    TargetTitle: '' as IntlString,
    TargetDescription: '' as IntlString,
    TargetSubmitterName: '' as IntlString,
    TargetSubmitterEmail: '' as IntlString,
    KindText: '' as IntlString,
    KindMultiline: '' as IntlString,
    KindEmail: '' as IntlString,
    KindNumber: '' as IntlString,
    KindSelect: '' as IntlString,
    PageHeading: '' as IntlString,
    PageIntro: '' as IntlString,
    PageSuccess: '' as IntlString,
    Notifications: '' as IntlString,
    AssignTo: '' as IntlString,
    AssignNobody: '' as IntlString,
    AssignHint: '' as IntlString,
    TelegramChatId: '' as IntlString,
    TelegramHint: '' as IntlString,
    NeedOneField: '' as IntlString,
    NeedOptions: '' as IntlString
  }
})
